export function moveArrayItem<T>(
  items: T[],
  fromIndex: number,
  toIndex: number
): T[] {
  // Handle edge cases
  if (items.length === 0) {
    return [];
  }

  if (fromIndex === toIndex) {
    return [...items];
  }

  // Validate indices
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length
  ) {
    return [...items];
  }

  // Extract the item to move
  // Safe to use non-null assertion since we've validated indices are in bounds
  const item = items[fromIndex]!;

  if (fromIndex < toIndex) {
    // Moving forward: elements shift down after removal
    // toIndex is the target position in the final array
    // [0..fromIndex) + [fromIndex+1..toIndex+1) + item + [toIndex+1..end)
    const before = items.slice(0, fromIndex);
    const between = items.slice(fromIndex + 1, toIndex + 1);
    const after = items.slice(toIndex + 1);
    return [...before, ...between, item, ...after];
  } else {
    // Moving backward: insert before removing
    // [0..toIndex) + item + [toIndex..fromIndex) + [fromIndex+1..end)
    const before = items.slice(0, toIndex);
    const middle = items.slice(toIndex, fromIndex);
    const after = items.slice(fromIndex + 1);
    return [...before, item, ...middle, ...after];
  }
}

export function clone<T extends object>(obj: T): T {
  return Object.entries(obj).reduce((acc: Partial<T>, [k, v]) => {
    const key = k as keyof T;
    const value = v as T[keyof T];

    if (Array.isArray(value)) {
      acc[key] = [...value].map(clone) as T[keyof T];
    } else if (typeof value === 'object' && value !== null) {
      acc[key] = clone(value);
    } else {
      acc[key] = value;
    }
    return acc;
  }, {} as Partial<T>) as T;
}
